import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Link,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  ContractStateDto,
  NoCompetitionCondition,
  type NoCompetitionJustificationDto,
  Regulation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
  useGetContractQuery,
  useListContractsQuery,
  NegotiationImpossibleCase,
  WindingUpCase,
  type FoaPart,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { ListLoadError } from "../../../../../common/ListLoadError";
import { SingleSelector } from "../../../../../common/input/Selector/SingleSelector";
import { urls } from "../../../../../urls";
import { FieldSaveError } from "./FieldSaveError";
import {
  extendsOriginalContract,
  isOriginalContractOverThreeYears,
  noCompetitionConditionsFor,
  negotiationCaseLabel,
  negotiationCaseProvision,
  negotiationImpossibleCasesFor,
  noCompetitionProvision,
  WINDING_UP_CASES,
  originalContractDate,
} from "../utils/noCompetition";

const TEXT_DEBOUNCE_MS = 400;
const JUSTIFICATION_PATH = "publicContext.noCompetitionJustification";
const CONTRACT_LIST_LIMIT = 100;

const limitedRationaleToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

interface Props {
  eventId: string;
  justification: NoCompetitionJustificationDto | null;
  regulation: Regulation | null;
  contestBelowEeaTier?: boolean;
  isEditable: boolean;
}

export const NoCompetitionJustificationField = ({ eventId, justification, regulation, contestBelowEeaTier, isEditable }: Props) => {
  const { t } = useTranslation();
  const { updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const condition = justification?.condition ?? null;
  const justificationStatus = fieldStatus(JUSTIFICATION_PATH);
  const isSaving = justificationStatus === "saving" || justificationStatus === "pending";

  const save = (changes: Partial<NoCompetitionJustificationDto>, debounceMs?: number) => {
    const chosen = changes.condition ?? condition;
    if (chosen == null) return;
    const next = {
      condition: chosen,
      rationale: changes.rationale ?? justification?.rationale ?? "",
      originalContractId: extendsOriginalContract(chosen)
        ? ("originalContractId" in changes ? changes.originalContractId : justification?.originalContractId) ?? null
        : null,
      repetitionForeseenInOriginalDocuments:
        chosen === NoCompetitionCondition.RepetitionOfSimilarWorks
          ? ("repetitionForeseenInOriginalDocuments" in changes
              ? changes.repetitionForeseenInOriginalDocuments
              : justification?.repetitionForeseenInOriginalDocuments) ?? null
          : null,
      negotiationImpossibleCase:
        chosen === NoCompetitionCondition.NegotiatedProcedureImpossible
          ? ("negotiationImpossibleCase" in changes
              ? changes.negotiationImpossibleCase
              : justification?.negotiationImpossibleCase) ?? null
          : null,
      windingUpCase:
        chosen === NoCompetitionCondition.WindingUpTerms
          ? ("windingUpCase" in changes ? changes.windingUpCase : justification?.windingUpCase) ?? null
          : null,
    };
    updateField({ publicContext: { noCompetitionJustification: next } }, debounceMs ? { debounceMs } : undefined);
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Heading as="h3" size="sm">
        {t("Grounds for a procurement without competition")} {isSaving && <Spinner size="xs" />}
      </Heading>
      <Text fontSize="sm" color="smMuted" mt="1">
        {t("A contract may only be awarded without competition when one of these conditions is met.")}
      </Text>

      <FormControl mt="5" isDisabled={!isEditable}>
        <FormLabel fontSize="sm">{t("Which condition applies?")}</FormLabel>
        <RadioGroup value={condition ?? ""} onChange={(value) => save({ condition: value as NoCompetitionCondition })}>
          <Stack spacing="3">
            {noCompetitionConditionsFor(regulation).map((option) => (
              <Radio key={option} value={option} colorScheme="teal" alignItems="flex-start">
                <Text fontSize="sm">{t(option)}</Text>
                <Text fontSize="xs" color="smMuted" fontStyle="italic">
                  {noCompetitionProvision(option, regulation, t, contestBelowEeaTier)}
                </Text>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </FormControl>

      {condition === NoCompetitionCondition.NegotiatedProcedureImpossible && (
        <FormControl mt="5" isDisabled={!isEditable} isRequired>
          <FormLabel fontSize="sm">{t("Which case makes even a negotiation without notice impossible?")}</FormLabel>
          <RadioGroup
            value={justification?.negotiationImpossibleCase ?? ""}
            onChange={(value) => save({ negotiationImpossibleCase: value as NegotiationImpossibleCase })}
          >
            <Stack spacing="3">
              {negotiationImpossibleCasesFor(regulation).map((option) => (
                <Radio key={option} value={option} colorScheme="teal" alignItems="flex-start">
                  <Text fontSize="sm">{negotiationCaseLabel(option, regulation, t)}</Text>
                  <Text fontSize="xs" color="smMuted" fontStyle="italic">
                    {negotiationCaseProvision(option, regulation, t, contestBelowEeaTier)}
                  </Text>
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
          <FormHelperText>{t("The intention notice and the contract award notice state this case.")}</FormHelperText>
        </FormControl>
      )}

      {condition === NoCompetitionCondition.WindingUpTerms && (
        <FormControl mt="5" isDisabled={!isEditable} isRequired>
          <FormLabel fontSize="sm">{t("Who is the contract made with?")}</FormLabel>
          <RadioGroup
            value={justification?.windingUpCase ?? ""}
            onChange={(value) => save({ windingUpCase: value as WindingUpCase })}
          >
            <Stack spacing="3">
              {WINDING_UP_CASES.map((option) => (
                <Radio key={option} value={option} colorScheme="teal">
                  <Text fontSize="sm">{t(option)}</Text>
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>
      )}

      <FormControl mt="5" isDisabled={!isEditable}>
        <FormLabel fontSize="sm">{t("Justification")}</FormLabel>
        {condition == null ? (
          <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="4">
            <Text fontSize="sm" color="smMuted">
              {t("Choose which condition applies before writing the justification.")}
            </Text>
          </Box>
        ) : (
          <TipTap
            content={justification?.rationale ?? ""}
            onUpdate={(rationale: string) => save({ rationale }, TEXT_DEBOUNCE_MS)}
            toolbar={limitedRationaleToolbar}
            debounceWait={0}
            editable={isEditable}
          />
        )}
        <FormHelperText>
          {t("Carried into the procurement protocol and the intention notice — you will not be asked to write it again.")}
        </FormHelperText>
      </FormControl>

      {extendsOriginalContract(condition) && (
        <OriginalContractField
          contractId={justification?.originalContractId ?? null}
          regulation={regulation}
          isEditable={isEditable}
          onChange={(originalContractId) => save({ originalContractId })}
        />
      )}

      {condition === NoCompetitionCondition.RepetitionOfSimilarWorks && (
        <FormControl mt="5" isDisabled={!isEditable}>
          <Checkbox
            colorScheme="teal"
            isChecked={justification?.repetitionForeseenInOriginalDocuments === true}
            onChange={(e) => save({ repetitionForeseenInOriginalDocuments: e.target.checked })}
          >
            <Text fontSize="sm">
              {t("Scope, terms and the reservation of a new contract were stated in the original procurement documents")}
            </Text>
          </Checkbox>
        </FormControl>
      )}

      <FieldSaveError eventId={eventId} path={JUSTIFICATION_PATH} />
    </Box>
  );
};

const OriginalContractField = ({
  contractId,
  regulation,
  isEditable,
  onChange,
}: {
  contractId: string | null;
  regulation: Regulation | null;
  isEditable: boolean;
  onChange: (contractId: string | null) => void;
}) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const [titleSearch, setTitleSearch] = useState("");
  const [searchedTitle, setSearchedTitle] = useState("");
  const searchTitle = useMemo(() => debounce((title: string) => setSearchedTitle(title.trim()), 300), []);
  useEffect(() => () => searchTitle.cancel(), [searchTitle]);
  const contracts = useListContractsQuery({
    orgId: authState.selectedOrg.id,
    state: ContractStateDto.Published,
    limit: CONTRACT_LIST_LIMIT,
    title: searchedTitle === "" ? undefined : searchedTitle,
  });
  const selected = useGetContractQuery({ contractId: contractId ?? "" }, { skip: contractId == null });
  const counted = selected.data != null ? originalContractDate(selected.data) : null;
  const overThreeYears = counted != null && isOriginalContractOverThreeYears(counted.date, new Date());
  const found = contracts.currentData?.contracts ?? [];
  const chosen = selected.data;
  const searchFailed = contracts.isError && contracts.data != null;
  const firstLoadFailed = contracts.isError && contracts.data == null;
  const registerIsEmpty =
    searchedTitle === "" && contracts.currentData != null && found.length === 0 && chosen == null;
  const countedFromDate = counted ? new Date(counted.date).toLocaleDateString("nb-NO") : "";

  return (
    <FormControl mt="5" isDisabled={!isEditable} isRequired>
      <FormLabel fontSize="sm">{t("Original contract")}</FormLabel>
      {contracts.isLoading ? (
        <Spinner size="sm" />
      ) : firstLoadFailed ? (
        <ListLoadError
          message={t("Could not load the contracts.")}
          onRetry={contracts.refetch}
          isRetrying={contracts.isFetching}
        />
      ) : registerIsEmpty ? (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Box>
            <Text>
              {t("The original contract must be in the contract register, and your organization has no published contracts there yet.")}
            </Text>
            <Link as={RouterLink} to={urls.contracts.viewAll} color="smPrimary">
              {t("Open the contract register")}
            </Link>
          </Box>
        </Alert>
      ) : (
        <>
          <SingleSelector
            size="sm"
            placeholder={{ text: t("Search the contract register by title"), color: "" }}
            isLoading={contracts.isFetching}
            isDisabled={!isEditable}
            value={chosen ? { label: chosen.title, value: chosen.id } : undefined}
            options={searchFailed ? [] : found.map((contract) => ({ value: contract.id, label: contract.title }))}
            noOptionsProvidedMessage={searchFailed ? String(t("Could not search the contract register.")) : undefined}
            noMatchingOptionsMessage={
              searchFailed
                ? String(t("Could not search the contract register."))
                : String(t("No published contract has «{{title}}» in its title.", { title: titleSearch.trim() }))
            }
            inputValue={titleSearch}
            onInputChange={(title, meta) => {
              if (meta.action === "input-blur" || meta.action === "menu-close") return;
              setTitleSearch(title);
              searchTitle(title);
            }}
            onChange={(option) => onChange(option?.value ?? null)}
          />
          {searchFailed && (
            <Flex align="center" gap="3" mt="2">
              <Text fontSize="sm" color="smError">
                {t("Could not search the contract register.")}
              </Text>
              <Button variant="outline" colorScheme="teal" size="xs" onClick={() => void contracts.refetch()}>
                {t("Try again")}
              </Button>
            </Flex>
          )}
          <FormHelperText>
            {found.length >= CONTRACT_LIST_LIMIT
              ? t("Showing the first {{count}} matches. Search by title to find the one you need.", {
                  count: CONTRACT_LIST_LIMIT,
                })
              : t("Not listed? Register and publish the original contract in the contract register first.")}{" "}
            <Link as={RouterLink} to={urls.contracts.viewAll} color="smPrimary">
              {t("Open the contract register")}
            </Link>
          </FormHelperText>
        </>
      )}
      {selected.isError && (
        <Box mt="3">
          <ListLoadError
            message={t("Could not load the original contract, so the three-year limit was not checked.")}
            onRetry={selected.refetch}
            isRetrying={selected.isFetching}
          />
        </Box>
      )}
      {counted?.countedFrom === "start" && (
        <FormHelperText>
          {t("The contract has no award date, so the three-year limit is counted from its start date ({{date}}).", {
            date: countedFromDate,
          })}
        </FormHelperText>
      )}
      {counted?.countedFrom === "registration" && (
        <FormHelperText>
          {t("The contract has no award date or start date, so the three-year limit is counted from the day it was registered ({{date}}).", {
            date: countedFromDate,
          })}
        </FormHelperText>
      )}
      {overThreeYears && (
        <Alert status="warning" mt="3" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {regulation === Regulation.Forsyningsforskriften
            ? t("The three-year limit is normally exceeded (forsyningsforskriften § 9-3 (c) and (d))")
            : t("The three-year limit is normally exceeded (FOA § 13-4 (c) and (d))")}
        </Alert>
      )}
    </FormControl>
  );
};
